var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from "react";
import { Button, SinglePriceCompare } from "@savvyinsure/react-ui";
import { ConnectIssuerIcon, SavvyIssuerIcon, } from "../../components/IssuerIcon";
import { convertCentsToDollars, shouldOpenInSameWindow } from "../../lib/util";
import AppBody from "../../components/AppBody";
import Header from "../../components/Header";
import { InsuranceType } from "../../constants";
import { ResultType } from "../../services/savvy/constants";
import { completedPage } from "../../lib/Avo";
import { findSavvyAdvertiser } from "../../utils/findSavvyAdvertiser";
import { getMonthlyAutoPremium } from "../../services/savvy/utils";
import handleResultClick from "../../utils/handleResultClick";
import shouldShowFallbackIcon from "../../utils/shouldShowFallbackIcon";
import { skipToken } from "@reduxjs/toolkit/query";
import useAffiliateConfig from "../../hooks/useAffiliateConfig";
import useAppSelector from "../../hooks/useAppSelector";
import { useGetSearchResultsQuery } from "../../services/savvy";
import useImpressionType from "../../services/savvy/useImpressionType";
import useIsRemarketingSearch from "../../features/configuration/useIsRemarketingSearch";
function BundleLinkoutCard(_a) {
    var current = _a.current, selected = _a.selected, title = _a.title, details = _a.details, cta = _a.cta;
    var savings = typeof (current === null || current === void 0 ? void 0 : current.price) === "number" && typeof (selected === null || selected === void 0 ? void 0 : selected.price) === "number"
        ? current.price - selected.price
        : 0;
    return (React.createElement("div", { className: "rounded-2xl border border-gray-200 px-4 py-6" },
        React.createElement("h2", { className: "mb-3 text-xl font-bold leading-none" }, title),
        React.createElement("div", { className: "-mx-4" },
            React.createElement(SinglePriceCompare, { current: current, selected: selected, divider: false })),
        React.createElement("div", { className: "space-y-3" },
            savings > 0 ? (React.createElement("p", { className: "text-center font-medium text-black-300" },
                "This switch saves you",
                " ",
                React.createElement("span", { className: "font-bold text-green" },
                    "$",
                    savings,
                    "/mo"),
                "!")) : null,
            React.createElement("p", { className: "text-center" }, details),
            cta ? React.createElement(Button, { onClick: cta.onClick }, cta.text) : null)));
}
export default function BundleReviewLinkout() {
    var _a, _b, _c, _d, _e, _f, _g;
    var commonAnalyticsProperties = useAppSelector(function (state) { return state.analytics; }).commonAnalyticsProperties;
    var _h = useAppSelector(function (state) { return state.applicant; }), bundle = _h.bundle, accountReferenceId = _h.accountReferenceId;
    var isRemarketingSearch = useIsRemarketingSearch();
    var impressionType = useImpressionType();
    var _j = useGetSearchResultsQuery(accountReferenceId
        ? {
            accountReferenceId: accountReferenceId,
            isRemarketingSearch: isRemarketingSearch,
            impressionType: impressionType,
        }
        : skipToken, {
        selectFromResult: function (_a) {
            var data = _a.data;
            return ({
                selectedBundle: data === null || data === void 0 ? void 0 : data.bundles.find(function (_a) {
                    var id = _a.id;
                    return id === bundle.id;
                }),
                currentPlan: data === null || data === void 0 ? void 0 : data.currentPlan,
            });
        },
    }), selectedBundle = _j.selectedBundle, currentPlan = _j.currentPlan;
    var hideIssuerLogos = useAffiliateConfig().hideIssuerLogos;
    var monthlyAutoPremium = getMonthlyAutoPremium(currentPlan);
    var autoMonthlyCost = convertCentsToDollars(monthlyAutoPremium);
    var sameAutoPolicy = (selectedBundle === null || selectedBundle === void 0 ? void 0 : selectedBundle.auto.type) === ResultType.CurrentAutoPolicy;
    var sameHomePolicy = (selectedBundle === null || selectedBundle === void 0 ? void 0 : selectedBundle.home.type) === ResultType.CurrentHomePolicy;
    var selectedHomePrice = convertCentsToDollars(selectedBundle === null || selectedBundle === void 0 ? void 0 : selectedBundle.home.quoteMonthlyCents);
    var selectedHomeIssuer = ((_a = selectedBundle === null || selectedBundle === void 0 ? void 0 : selectedBundle.home) === null || _a === void 0 ? void 0 : _a.issuerId)
        ? {
            id: (_b = selectedBundle === null || selectedBundle === void 0 ? void 0 : selectedBundle.home) === null || _b === void 0 ? void 0 : _b.issuerId,
            name: (_c = selectedBundle === null || selectedBundle === void 0 ? void 0 : selectedBundle.home) === null || _c === void 0 ? void 0 : _c.issuerName,
        }
        : findSavvyAdvertiser(((_d = selectedBundle === null || selectedBundle === void 0 ? void 0 : selectedBundle.home) === null || _d === void 0 ? void 0 : _d.advertiserName) || "");
    function sendCompletePageEvent(id) {
        var clickUrl = selectedBundle === null || selectedBundle === void 0 ? void 0 : selectedBundle.clickUrl;
        var openInSameWindow = shouldOpenInSameWindow();
        completedPage(__assign({ selectedOption: "Verify Information", optionSelected: "Verify Information" }, commonAnalyticsProperties));
        if (clickUrl) {
            handleResultClick("".concat(clickUrl, "?id=").concat(id), openInSameWindow);
        }
    }
    if (!selectedBundle) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Header, null),
        React.createElement(AppBody, { className: "overflow-y-auto overflow-x-hidden px-4 pb-4" },
            React.createElement("h1", { className: "mb-1 text-2xl font-bold leading-none" }, "Your Selected Policies"),
            React.createElement("p", { className: "mb-4 text-black-300" }, "Select the first policy you want to shop. Don't forget to come back to this page to shop both offers."),
            React.createElement("div", { className: "flex flex-col space-y-4" },
                React.createElement(BundleLinkoutCard, { title: "Auto Insurance", current: {
                        price: autoMonthlyCost,
                        logo: (React.createElement(ConnectIssuerIcon, { issuerId: currentPlan === null || currentPlan === void 0 ? void 0 : currentPlan.issuerId, fallback: shouldShowFallbackIcon(hideIssuerLogos, currentPlan === null || currentPlan === void 0 ? void 0 : currentPlan.issuerId), insuranceType: InsuranceType.Auto })),
                    }, selected: !sameAutoPolicy
                        ? {
                            price: convertCentsToDollars((_e = selectedBundle === null || selectedBundle === void 0 ? void 0 : selectedBundle.auto) === null || _e === void 0 ? void 0 : _e.quoteMonthlyCents),
                            logo: (React.createElement(SavvyIssuerIcon, { issuerId: (_f = selectedBundle === null || selectedBundle === void 0 ? void 0 : selectedBundle.auto) === null || _f === void 0 ? void 0 : _f.issuerId, insuranceType: InsuranceType.Auto })),
                            title: (_g = selectedBundle === null || selectedBundle === void 0 ? void 0 : selectedBundle.auto) === null || _g === void 0 ? void 0 : _g.issuerName,
                        }
                        : undefined, details: sameAutoPolicy
                        ? "You selected to keep your current auto policy."
                        : "Continue with ".concat(selectedBundle.auto.issuerName, "."), cta: sameAutoPolicy
                        ? undefined
                        : {
                            onClick: function () {
                                return sendCompletePageEvent(selectedBundle.auto.id);
                            },
                            text: "Next",
                        } }),
                React.createElement(BundleLinkoutCard, { title: "Home Insurance", current: {
                        price: convertCentsToDollars(currentPlan === null || currentPlan === void 0 ? void 0 : currentPlan.homeMonthlyPremiumCents),
                        logo: (React.createElement(ConnectIssuerIcon, { issuerId: currentPlan === null || currentPlan === void 0 ? void 0 : currentPlan.homeIssuerId, fallback: shouldShowFallbackIcon(hideIssuerLogos, currentPlan === null || currentPlan === void 0 ? void 0 : currentPlan.homeIssuerId), insuranceType: InsuranceType.Home })),
                    }, selected: !sameHomePolicy
                        ? {
                            actionText: !selectedHomePrice ? "Check Offer" : undefined,
                            price: selectedHomePrice || undefined,
                            logo: (React.createElement(SavvyIssuerIcon, { issuerId: selectedHomeIssuer === null || selectedHomeIssuer === void 0 ? void 0 : selectedHomeIssuer.id, insuranceType: InsuranceType.Home })),
                            title: (selectedHomeIssuer === null || selectedHomeIssuer === void 0 ? void 0 : selectedHomeIssuer.name) || "",
                        }
                        : undefined, details: sameHomePolicy
                        ? "You selected to keep your current home policy."
                        : "Continue with ".concat(selectedHomeIssuer === null || selectedHomeIssuer === void 0 ? void 0 : selectedHomeIssuer.name, "."), cta: {
                        onClick: function () { var _a; return sendCompletePageEvent((_a = selectedBundle === null || selectedBundle === void 0 ? void 0 : selectedBundle.home) === null || _a === void 0 ? void 0 : _a.id); },
                        text: "Next",
                    } })))));
}
